import SettingsAddUserModal from "@/components/SettingsAddUserModal/SettingsAddUserModal.vue";
import {
    client_app,
    create_user_url,
    get_auth_headers,
    get_response_data,
    get_user_friendly_error_message
} from "../settings_user_management_api";

export default {
    name: "SettingsUserManagementAddUser",

    components: {
        SettingsAddUserModal
    },

    props: {
        is_open: {
            type: Boolean,
            default: false
        },
        users: {
            type: Array,
            default: () => []
        },
        role_options: {
            type: Array,
            default: () => []
        },
        is_loading_roles: {
            type: Boolean,
            default: false
        }
    },

    emits: ["close", "user-added", "reload-users"],

    data() {
        return ({
            is_saving: false,
            form_error: ""
        });
    },

    watch: {
        is_open(next_value) {
            if (next_value) {
                this.form_error = "";
            }
        }
    },

    methods: {
        close_modal() {
            this.form_error = "";
            this.$emit("close");
        },

        validate_user_payload(payload) {
            if (!payload.first_name || !payload.last_name || !payload.email) {
                return "First name, last name, and email are required.";
            }

            if (!payload.role_id) {
                return "Select a role for this user.";
            }

            return "";
        },

        get_role_id(role) {
            return role?.id ?? null;
        },

        get_form_role_id(form_payload) {
            return form_payload.role_id ?? this.get_role_id(form_payload.role);
        },

        get_user_payload(form_payload) {
            const role_id = this.get_form_role_id(form_payload);
            const payload = {
                first_name: form_payload.first_name,
                last_name: form_payload.last_name,
                email: form_payload.email,
                phone_number: form_payload.phone_number ?? "",
                username: form_payload.username ?? form_payload.email,
                is_active: form_payload.is_active !== false,
                session_policy: form_payload.session_policy || "default",
                session_timeout_minutes: form_payload.session_policy === "custom" ? Number(form_payload.session_timeout_minutes) : null,
                client_app
            };

            if (role_id) {
                payload.role_id = role_id;
            }

            return payload;
        },

        async save_user(form_payload) {
            const validation_error = this.validate_user_payload(form_payload);

            if (validation_error) {
                this.form_error = get_user_friendly_error_message(validation_error);
                return;
            }

            this.is_saving = true;
            this.form_error = "";

            try {
                const payload = this.get_user_payload(form_payload);
                let local_user = null;

                if (create_user_url) {
                    await this.save_user_to_api(payload);
                } else {
                    local_user = this.get_local_user(form_payload, payload);
                }

                this.$emit("user-added", "User added successfully.", local_user);
                this.close_modal();
            } catch (error) {
                this.form_error = get_user_friendly_error_message(error?.message, "We could not save this user. Please try again.");
            } finally {
                this.is_saving = false;
            }
        },

        async save_user_to_api(payload) {
            const response = await fetch(create_user_url, {
                method: "POST",
                headers: get_auth_headers(true),
                body: JSON.stringify(payload)
            });
            await get_response_data(response, "Unable to save user.");

            await this.emit_reload_users();
        },

        emit_reload_users() {
            return new Promise((resolve) => {
                this.$emit("reload-users", resolve);
            });
        },

        get_local_user(form_payload, payload) {
            return ({
                id: `local-${Date.now()}`,
                first_name: payload.first_name,
                last_name: payload.last_name,
                username: payload.username,
                email: payload.email,
                phone_number: payload.phone_number,
                role: form_payload.role?.name || "",
                role_id: payload.role_id,
                property_ids: [],
                assigned_properties: [],
                is_active: payload.is_active
            });
        }
    }
};
